import {
  Button,
  Box,
  Flex,
  InputGroup,
  InputLeftAddon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  Text,
  Link,
} from '@televet/kibble-ui';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import React, { useEffect, useState } from 'react';
import {
  ClinicPhoneNumberCreateInput,
  GetClinicPhoneNumbersQuery,
  PhoneNumberStatus,
  ServiceCapability,
  ServiceProvider,
  useCreateOneClinicPhoneNumberMutation,
  useTwilioEligibilityCheckLazyQuery,
} from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { cleanPhoneNumber, validatePhoneNumber } from 'shared/utils';
import { ApolloQueryResult } from '@apollo/client';
import { env } from 'env';

interface IAddClinicPhoneNumberButtonProps {
  totalNumbers: number;
  onAddNumberError: () => void;
  refetchPhoneNumbers: () => Promise<ApolloQueryResult<GetClinicPhoneNumbersQuery>>;
  isClinicDataComplete: boolean;
}

function transformToReadable(text: string): string {
  return text
    .toLowerCase() // Convert to lowercase
    .replace(/_|-/g, ' ') // Replace underscores or hyphens with spaces
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
}

function getErrorColor(isEligible: boolean, requiresManualIntervention: boolean): string {
  return isEligible && !requiresManualIntervention
    ? 'text.success'
    : requiresManualIntervention
    ? 'text.warning'
    : 'text.danger';
}
const initialEligibility = {
  isEligible: false,
  phoneType: '',
  status: '',
  subStatus: '',
  reason: '',
  requiresManualIntervention: false,
  nextStep: '',
  fetchedData: false,
  ticketCreated: false,
  existingZdTicketUrl: '',
};
const AddTwilioClinicPhoneNumberButton = ({
  totalNumbers,
  onAddNumberError,
  refetchPhoneNumbers,
  isClinicDataComplete,
}: IAddClinicPhoneNumberButtonProps): JSX.Element => {
  const { currentClinicId } = useClinicUser();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [createClinicPhoneNumberErrorMessage, setCreateClinicPhoneNumberErrorMessage] = useState('');
  const [numberToCreate, setNumberToCreate] = useState('');

  const [getTwilioEligibility, { data: eligibilityData, loading: isEligibilityLoading }] =
    useTwilioEligibilityCheckLazyQuery({
      variables: {
        data: {
          clinicId: currentClinicId,
          phoneNumber: numberToCreate,
        },
      },
    });

  const [eligibility, setEligibility] = useState(initialEligibility);

  useEffect(() => {
    const fullEligibilityReport = eligibilityData?.twilioEligibilityCheck?.payload;
    const eligibilityResult = fullEligibilityReport?.eligibility.results[0];

    const status = eligibilityResult?.eligibility_status ?? '';
    const subStatus = eligibilityResult?.eligibility_sub_status ?? '';
    const ineligibilityReason = eligibilityResult?.ineligibility_reason ?? '';
    const phoneType = eligibilityResult?.phone_number_type ?? '';
    const nextStep = eligibilityResult?.next_step ?? '';
    const existingZdTicket = fullEligibilityReport?.ticketUrl;
    const requiresManualIntervention =
      subStatus.toLowerCase() === 'eligible_by_manual_process' ||
      subStatus.toLowerCase() === 'may_be_eligible_by_manual_process';

    const isEligible = (status === 'ELIGIBLE' && subStatus === 'ELIGIBLE') || requiresManualIntervention;
    setEligibility({
      isEligible,
      requiresManualIntervention,
      phoneType: transformToReadable(phoneType),
      status: transformToReadable(status),
      subStatus: transformToReadable(subStatus),
      reason: transformToReadable(ineligibilityReason),
      nextStep: transformToReadable(nextStep),
      ticketCreated: fullEligibilityReport?.ticketCreated,
      existingZdTicketUrl: existingZdTicket,
      fetchedData: !!eligibilityData?.twilioEligibilityCheck?.payload,
    });
  }, [eligibilityData?.twilioEligibilityCheck?.payload, eligibilityData?.twilioEligibilityCheck?.payload?.results]);

  const [createOneClinicPhoneNumber] = useCreateOneClinicPhoneNumberMutation();

  const handleAddClinicPhoneNumberClick = (): void => {
    onOpen();
  };

  const resetState = (): void => {
    setCreateClinicPhoneNumberErrorMessage('');
    setNumberToCreate('');
    setEligibility(initialEligibility);
  };

  const handleCheckEligibility = async (): Promise<void> => {
    const phoneValidation = validatePhoneNumber(numberToCreate) && cleanPhoneNumber(numberToCreate).length === 10;
    if (!phoneValidation) {
      setCreateClinicPhoneNumberErrorMessage('Please enter a valid phone number');
      return;
    }
    setCreateClinicPhoneNumberErrorMessage('');
    await getTwilioEligibility({
      variables: {
        data: {
          phoneNumber: `+1${cleanPhoneNumber(numberToCreate)}`,
          clinicId: currentClinicId,
        },
      },
    });
  };

  const handleCreateClinicPhoneNumber = async (): Promise<void> => {
    const phoneValidation = validatePhoneNumber(numberToCreate) && cleanPhoneNumber(numberToCreate).length === 10;
    try {
      if (!phoneValidation) {
        setCreateClinicPhoneNumberErrorMessage('Please enter a valid phone number');
        return;
      }
      setCreateClinicPhoneNumberErrorMessage('');
      const data: ClinicPhoneNumberCreateInput = {
        clinic: { connect: { id: currentClinicId || '' } },
        number: `+1${cleanPhoneNumber(numberToCreate)}`,
        provider: ServiceProvider.Twilio,
        capability: ServiceCapability.Sms,
        status: PhoneNumberStatus.New,
        isPrimary: false,
      };

      await createOneClinicPhoneNumber({
        variables: {
          data,
        },
      });
      await refetchPhoneNumbers();
      resetState();
      onClose();
    } catch {
      setCreateClinicPhoneNumberErrorMessage(
        'Unable to create phone number, probably phone number with SMS capability already exists, please create a Zendesk ticket',
      );
    }
  };
  return (
    <div>
      <Button
        size="sm"
        variant={'secondary'}
        iconName={'plus'}
        onClick={(): void => {
          if (totalNumbers >= 2 || !isClinicDataComplete) {
            onAddNumberError();
          } else {
            handleAddClinicPhoneNumberClick();
          }
        }}
      >
        Add New Number
      </Button>
      <Modal
        isOpen={isOpen}
        onClose={(): void => {
          setCreateClinicPhoneNumberErrorMessage('');
          resetState();
          onClose();
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Number</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Please add a valid phone number (123)-456-7890
            <Flex justifyContent={'center'} marginTop="20px">
              <InputGroup width={'200px'}>
                <InputLeftAddon>+1</InputLeftAddon>
                <TextInput
                  isDisabled={isEligibilityLoading || (eligibility.isEligible && !!eligibility.status)}
                  type="tel"
                  value={numberToCreate}
                  onChange={(event): void => {
                    setNumberToCreate(event.currentTarget.value);
                  }}
                  placeholder="phone number"
                />
              </InputGroup>
            </Flex>
            {createClinicPhoneNumberErrorMessage && (
              <Box marginTop={'10px'} color="text.danger">
                {createClinicPhoneNumberErrorMessage}
              </Box>
            )}
            <Box mt={4}>
              {isEligibilityLoading && <Box>Checking eligibility...</Box>}
              {eligibility.fetchedData && (
                <Box>
                  <Text as="p" color={getErrorColor(eligibility.isEligible, eligibility.requiresManualIntervention)}>
                    Number is {eligibility.isEligible ? '' : 'not'} eligible
                    {eligibility.requiresManualIntervention ? ', with warnings' : ''}
                  </Text>
                </Box>
              )}
              {(!eligibility.isEligible && eligibility.fetchedData) || eligibility.requiresManualIntervention ? (
                <Box>
                  <Text as="p" color={getErrorColor(eligibility.isEligible, eligibility.requiresManualIntervention)}>
                    Reason: <Text>{eligibility.reason}</Text>
                  </Text>
                  <Text as="p" color={getErrorColor(eligibility.isEligible, eligibility.requiresManualIntervention)}>
                    Next Step: <Text>{eligibility.nextStep}</Text>
                  </Text>
                  <Text as="p" color={getErrorColor(eligibility.isEligible, eligibility.requiresManualIntervention)}>
                    Phone Type: <Text>{eligibility.phoneType}</Text>
                  </Text>
                  <Text as="p" color={getErrorColor(eligibility.isEligible, eligibility.requiresManualIntervention)}>
                    Status: <Text>{eligibility.status}</Text>
                  </Text>
                  <Text as="p" color={getErrorColor(eligibility.isEligible, eligibility.requiresManualIntervention)}>
                    Sub Status: <Text>{eligibility.subStatus}</Text>
                  </Text>
                  {eligibility.ticketCreated && eligibility.existingZdTicketUrl !== '' ? (
                    <>
                      <br />
                      <hr></hr>
                      <br />
                      <Text as="p">
                        ZD Ticket Created:{' '}
                        <Link href={eligibility?.existingZdTicketUrl} target="_blank" rel="noreferrer">
                          {eligibility?.existingZdTicketUrl}
                        </Link>
                      </Text>
                    </>
                  ) : (
                    <Text as="p">ZD Ticket Not Created</Text>
                  )}
                </Box>
              ) : (
                ''
              )}
            </Box>
          </ModalBody>
          <ModalFooter marginBottom={'8px'}>
            {eligibility.isEligible ? (
              <Button onClick={handleCreateClinicPhoneNumber}>Add Number</Button>
            ) : (
              <>
                <Button onClick={handleCheckEligibility}>Check Eligibility</Button>
                {env.REACT_APP_PRODUCTION != 'true' ? (
                  <Button
                    style={{ background: 'rgb(255,200,145)', marginLeft: '5px' }}
                    onClick={(): void => {
                      const phoneValidation =
                        validatePhoneNumber(numberToCreate) && cleanPhoneNumber(numberToCreate).length === 10;
                      if (!phoneValidation) {
                        setCreateClinicPhoneNumberErrorMessage('Please enter a valid phone number');
                        return;
                      }
                      setCreateClinicPhoneNumberErrorMessage('');
                      setEligibility({
                        isEligible: true,
                        requiresManualIntervention: true,
                        phoneType: 'Unknown',
                        status: transformToReadable('INELIGIBLE'),
                        subStatus: transformToReadable('eligible_by_manual_process'),
                        reason: transformToReadable('eligible_for_hosting_by_manual_process'),
                        nextStep: transformToReadable('contact_support_for_manual_process'),
                        ticketCreated: false,
                        existingZdTicketUrl: '',
                        fetchedData: true,
                      });
                    }}
                  >
                    Test Custom Eligibility
                  </Button>
                ) : (
                  ''
                )}
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default AddTwilioClinicPhoneNumberButton;
